// Component to display the most important Job info as a card format
import React, { MouseEvent, useRef, useState } from 'react';

import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';

import { size } from 'lodash';

import {
  getCanUserEditAllJobAssignments,
  getJobRequiresAttention,
} from '@isd/lib/firebase/db/helpers';
import { JobDoc } from '@isd/lib/firebase/db/metaTypes';

import { useShouldScroll } from '@isd/lib/helpers/hooks';

import { useAuthContext } from '@/context/AuthContext';
import { useEditSchedulerContext } from '@/context/EditSchedulerContext';
import { useSchedulerContext } from '@/context/SchedulerContext';

import JobGroups from '@/components/Scheduler/SchedulerMain/JobCard/JobGroups';
import JobInfo from '@/components/Scheduler/SchedulerMain/JobCard/JobInfo';

export default function JobCard({ jobDoc }: { jobDoc: JobDoc }) {
  const { userDoc } = useAuthContext();
  const { showOperators } = useSchedulerContext();
  const {
    canUserEditDispatchSchedule,
    selectedJob,
    onSelectJob,
    onClearSelectedJob,
    isConfirmChangesDisabled,
  } = useEditSchedulerContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const doesJobRequiresAttention = getJobRequiresAttention(jobDoc);
  const isJobIncomplete = jobDoc.get('overallStatus') === 'INCOMPLETE';
  const jobGroupsContainer = useRef<HTMLDivElement | null>(null);
  const shouldScroll = useShouldScroll(jobGroupsContainer);

  const showGroups = showOperators || isExpanded;
  const groupCount = size(jobDoc.get('groupsOfOperators'));

  const isSelectedJob = selectedJob?.id === jobDoc.id;

  const isSelectable =
    !isSelectedJob &&
    canUserEditDispatchSchedule &&
    !!userDoc &&
    getCanUserEditAllJobAssignments(userDoc, jobDoc) &&
    groupCount >= 1;

  const onClickJobCard = (jobDoc: JobDoc) => (ev: MouseEvent) => {
    const clickTarget = ev.target as HTMLElement;
    // Check that user has not clicked another actionable element of JobCard
    const actionableItemsSelectors = [
      '.add-operators-link',
      '.job-title',
      '.unfold-operators-button',
      '.job-assignment-info .clickable',
      '.edit-broker-button',
      '.job-broker-company-selector',
      '.pin-job-button',
    ];
    const hasClickedActionableItem = actionableItemsSelectors.some(
      (selector) => !!clickTarget.closest(selector)
    );
    if (!hasClickedActionableItem) {
      onSelectJob(jobDoc);
    }
  };

  return (
    <div className="job-card-container relative">
      {isSelectedJob ? (
        <div
          className={clsx(
            'unselect-job-btn btn btn-outline btn-sm bg-base-100 absolute -top-10 right-0 z-2',
            isConfirmChangesDisabled && 'btn-disabled'
          )}
          onClick={() => onClearSelectedJob()}
        >
          <div className="tooltip" data-tip="Unselect Job ">
            <XMarkIcon className="size-6" />
          </div>
        </div>
      ) : (
        !!selectedJob && (
          <div className="job-card-overlay absolute z-2 h-full w-full opacity-20"></div>
        )
      )}
      <div
        className={clsx(
          'job-card card bg-base-100 max-h-fit rounded-2xl',
          doesJobRequiresAttention &&
            `outline-4 outline-offset-0 ${isJobIncomplete ? 'outline-primary' : 'outline-info'}`,
          isSelectable && 'clickable',
          !!selectedJob && !isSelectedJob
            ? 'opacity-20'
            : 'shadow-lg drop-shadow-lg'
        )}
        onClick={isSelectable ? onClickJobCard(jobDoc) : undefined}
      >
        <div
          className={clsx(
            'job-card-upper bg-accent-dark text-base-100 px-3 py-6 transition-[border-radius] duration-300',
            showGroups ? 'rounded-t-2xl' : 'rounded-2xl'
          )}
        >
          <JobInfo jobDoc={jobDoc} />
          {!showOperators && (
            <UnfoldOperatorsGroupsButton
              onClick={() => setIsExpanded(!isExpanded)}
              isExpanded={isExpanded}
            />
          )}
        </div>
        <div
          ref={jobGroupsContainer}
          className={clsx(
            'job-card-lower overflow-hidden rounded-b-2xl transition-[max-height] duration-300 ease-in-out',
            // Use any arbitrarily high value for max height to force animation when changing to 0
            showGroups ? 'max-h-96' : 'max-h-0',
            shouldScroll && 'overflow-y-scroll',
            groupCount > 1 && 'bg-accent-dark'
          )}
        >
          <JobGroups jobDoc={jobDoc} />
        </div>
      </div>
    </div>
  );
}

function UnfoldOperatorsGroupsButton({
  onClick,
  isExpanded,
}: {
  onClick: React.MouseEventHandler;
  isExpanded: boolean;
}) {
  return (
    <div className="unfold-operators-button absolute left-0 w-full opacity-0 hover:opacity-100">
      <div
        className="clickable flex items-center justify-center"
        onClick={onClick}
      >
        <div className="btn btn-ghost btn-xs btn-block rounded-xl">
          <ChevronDownIcon
            className={clsx(
              'size-6 transition duration-300',
              isExpanded && 'scale-y-[-1]'
            )}
          />
        </div>
      </div>
    </div>
  );
}
